import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useProductPlacementsQuery,
  useSaveProductPlacementsMutation
} from "@queries/website";
import "@styles/opencart-admin.css";

// Where each placement tag surfaces on the website
const PLACEMENT_INFO = {
  landing: { label: "Home Page", hint: "Shop CTA section on the homepage (top 3 shown)" },
  b2b: { label: "B2B Page", hint: "Bulk catalogue on the B2B Care page" },
  featured: { label: "Featured", hint: "Promoted / featured surfaces" }
};

export default function ProductPlacementsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching, isError, error } = useProductPlacementsQuery();
  const saveMutation = useSaveProductPlacementsMutation();

  const [savingId, setSavingId] = React.useState(null);

  const placements = data?.placements || [];
  const products = data?.products || [];

  async function toggle(product, placement) {
    const current = product.placements || [];
    const next = current.includes(placement)
      ? current.filter((p) => p !== placement)
      : [...current, placement];

    setSavingId(product.product_id);
    try {
      await saveMutation.mutateAsync({
        productId: product.product_id,
        placements: next
      });
      queryClient.invalidateQueries({ queryKey: ["website", "product-placements"] });
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to save placement");
    } finally {
      setSavingId(null);
    }
  }

  const countFor = (placement) =>
    products.filter((p) => (p.placements || []).includes(placement)).length;

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Product Placement</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Website</span>
          <span className="sep">›</span>
          <span>Product Placement</span>
        </nav>
      </div>

      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Which product shows on which website page</strong>
            <div style={{ fontSize: 12, color: "#777" }}>
              Tick a page to show the product there — saved instantly. This only writes the
              website placement table; the core product record is never modified.
            </div>
          </div>
        </div>

        {isError && (
          <div style={{ color: "#c00", marginBottom: 8 }}>
            Error: {error?.message || "Unknown error"}
          </div>
        )}

        <table className="oc-table">
          <thead>
            <tr>
              <th>Product</th>
              <th style={{ width: 110 }}>Price</th>
              <th style={{ width: 90 }}>Status</th>
              {placements.map((pl) => (
                <th key={pl} style={{ width: 130, textAlign: "center" }}>
                  {PLACEMENT_INFO[pl]?.label || pl}
                  <div style={{ fontSize: 11, fontWeight: 400, color: "#777" }}>
                    {countFor(pl)} shown
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.length === 0 && (
              <tr>
                <td colSpan={3 + placements.length} style={{ textAlign: "center", padding: 24 }}>
                  No shop products
                </td>
              </tr>
            )}
            {products.map((p) => (
              <tr key={p.product_id} style={{ opacity: savingId === p.product_id ? 0.5 : 1 }}>
                <td>
                  {p.name || `Product #${p.product_id}`}
                  <div style={{ fontSize: 12, color: "#777" }}>
                    #{p.product_id} · {p.model}
                  </div>
                </td>
                <td>{Number(p.price).toFixed(2)}</td>
                <td>{p.status ? "Enabled" : "Disabled"}</td>
                {placements.map((pl) => (
                  <td key={pl} style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      style={{ width: 18, height: 18, cursor: "pointer" }}
                      checked={(p.placements || []).includes(pl)}
                      disabled={savingId === p.product_id}
                      onChange={() => toggle(p, pl)}
                      title={PLACEMENT_INFO[pl]?.hint || pl}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 12, fontSize: 12, color: "#777" }}>
          {placements.map((pl) => (
            <div key={pl}>
              <strong>{PLACEMENT_INFO[pl]?.label || pl}:</strong>{" "}
              {PLACEMENT_INFO[pl]?.hint || "—"}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
